import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, notification } from 'antd';
import axios from 'axios';

import { IInvestimentoACadastrar, IFonteInvestimento } from '../../interfaces';

import InvestimentosACadastrarService from '../../../../../services/http/services/SaldoDeContas/InvestimentosACadastrarService';
import { EmitirErro } from '../../../../../services/EmitirErro';

export interface IUseInvestimentoFormModalProps {
    visible: boolean;
    investimento: IInvestimentoACadastrar | null;
    onClose: () => void;
    onInvestimentoCreated: (investimentoACadastrarId: number) => void;
}

export const useInvestimentoFormModal = ({
    visible,
    investimento,
    onClose,
    onInvestimentoCreated,
}: IUseInvestimentoFormModalProps) => {
    const [form] = Form.useForm();
    const [loadingFontes, setLoadingFontes] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [fontes, setFontes] = useState<IFonteInvestimento[]>([]);
    const [fonteSelecionadaNumero, setFonteSelecionadaNumero] = useState<string | null>(null);

    const carregarFontes = useCallback(async (investimentoId: number) => {
        const source = axios.CancelToken.source();
        setLoadingFontes(true);
        setFontes([]);
        setFonteSelecionadaNumero(null);

        try {
            const data = await InvestimentosACadastrarService.getFontesInvestimento(investimentoId, source.token);
            const fetchedFontes = data || [];
            setFontes(fetchedFontes);

            if (fetchedFontes.length === 1) {
                const unicaFonte = fetchedFontes[0];
                setFonteSelecionadaNumero(unicaFonte.numero);
                form.setFieldsValue({ fonteNumero: unicaFonte.numero });

                if (unicaFonte.contas && unicaFonte.contas.length === 1) {
                    form.setFieldsValue({ contaId: unicaFonte.contas[0].id });
                }
            }
        } catch (error) {
            EmitirErro(error);
        } finally {
            setLoadingFontes(false);
        }
    }, [form]);

    useEffect(() => {
        if (visible && investimento) {
            form.resetFields();
            setFonteSelecionadaNumero(null);
            carregarFontes(investimento.id);
        } else {
            setFontes([]);
            setFonteSelecionadaNumero(null);
            form.resetFields();
        }
    }, [visible, investimento, carregarFontes, form]);

    const handleChangeFonte = useCallback((numero: string) => {
        setFonteSelecionadaNumero(numero || null);
        const fonte = fontes.find((f) => f.numero === numero);
        if (fonte && fonte.contas && fonte.contas.length === 1) {
            form.setFieldsValue({ contaId: fonte.contas[0].id });
        } else {
            form.setFieldsValue({ contaId: undefined });
        }
    }, [fontes, form]);

    const contasDisponiveis = useMemo(() => {
        if (!fonteSelecionadaNumero) return [];
        const fonte = fontes.find((f) => f.numero === fonteSelecionadaNumero);
        return fonte?.contas || [];
    }, [fontes, fonteSelecionadaNumero]);

    const handleFinish = useCallback(async (values: { contaId: number }) => {
        if (!investimento) return;

        setSubmitting(true);
        try {
            await InvestimentosACadastrarService.createInvestimento({
                investimentoACadastrarId: investimento.id,
                contaId: values.contaId,
            });

            notification.success({
                message: 'Sucesso',
                description: 'Investimento cadastrado com sucesso!',
            });

            onInvestimentoCreated(investimento.id);
            onClose();
        } catch (error) {
            EmitirErro(error);
        } finally {
            setSubmitting(false);
        }
    }, [investimento, onInvestimentoCreated, onClose]);

    const totalContas = useMemo(
        () => fontes.reduce((acc, fonte) => acc + (fonte.contas?.length || 0), 0),
        [fontes]
    );

    return {
        form,
        loadingFontes,
        submitting,
        fontes,
        fonteSelecionadaNumero,
        contasDisponiveis,
        handleChangeFonte,
        totalContas,
        handleFinish,
    };
};
